import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { managerApi, type UpdatePermissionsPayload } from '../api/managerApi';
import toast from 'react-hot-toast';

/**
 * Hook to fetch manager permissions
 */
export const useManagerPermissions = (managerId: string) => {
  return useQuery({
    queryKey: ['manager-permissions', managerId],
    queryFn: () => managerApi.getManagerPermissions(managerId),
    enabled: !!managerId,
  });
};

/**
 * Hook to update manager permissions
 */
export const useUpdateManagerPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      managerId,
      payload,
    }: {
      managerId: string;
      payload: UpdatePermissionsPayload;
    }) => managerApi.updateManagerPermissions(managerId, payload),
    onSuccess: (_data, variables) => {
      // Invalidate and refetch permissions
      queryClient.invalidateQueries({
        queryKey: ['manager-permissions', variables.managerId],
      });

      // Also invalidate manager details to update the full manager object
      queryClient.invalidateQueries({
        queryKey: ['manager-details', variables.managerId],
      });

      toast.success('Manager permissions have been updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update permissions');
    },
  });
};
